import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Highlight } from '@tiptap/extension-highlight';
import { Link } from '@tiptap/extension-link';
import { Box, Button, ButtonGroup, Divider, TextField, IconButton, Menu } from '../ui';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatStrikethrough as StrikeIcon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as OrderedListIcon,
  FormatQuote as BlockquoteIcon,
  Code as CodeIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  FormatAlignJustify as AlignJustifyIcon,
  Link as LinkIcon,
  FormatColorText as TextColorIcon,
  FormatColorFill as HighlightIcon,
  Code as HtmlIcon,
} from '@mui/icons-material';

interface TiptapEditorProps {
  content: string;
  onChange?: (html: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  showToolbar?: boolean;
  sx?: any;
}

const TiptapEditor: React.FC<TiptapEditorProps> = ({
  content,
  onChange,
  readOnly = false,
  placeholder = 'Start typing...',
  showToolbar = true,
  sx = {},
}) => {
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [htmlSource, setHtmlSource] = useState('');
  const [textColorAnchor, setTextColorAnchor] = useState<null | HTMLElement>(null);
  const [highlightAnchor, setHighlightAnchor] = useState<null | HTMLElement>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
    ],
    content: content,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  const toggleHtmlView = () => {
    if (showHtmlSource) {
      editor?.commands.setContent(htmlSource);
      setShowHtmlSource(false);
    } else {
      setHtmlSource(editor?.getHTML() || '');
      setShowHtmlSource(true);
    }
  };

  const colors = [
    '#000000', '#e60000', '#ff9900', '#ffff00', '#008a00', '#0066cc', '#9933ff',
    '#ffffff', '#facccc', '#ffebcc', '#ffffcc', '#cce8cc', '#cce0f5', '#ebd6ff',
    '#bbbbbb', '#f06666', '#ffc266', '#ffff66', '#66b966', '#66a3e0', '#c285ff',
    '#888888', '#a10000', '#b26b00', '#b2b200', '#006100', '#0047b2', '#6b24b2',
    '#444444', '#5c0000', '#663d00', '#666600', '#003700', '#002966', '#3d1466',
  ];

  const setTextColor = (color: string) => {
    editor?.chain().focus().setColor(color).run();
    setTextColorAnchor(null);
  };

  const setHighlightColor = (color: string) => {
    editor?.chain().focus().setHighlight({ color }).run();
    setHighlightAnchor(null);
  };

  const setLink = () => {
    if (linkUrl) {
      editor?.chain().focus().setLink({ href: linkUrl }).run();
    } else {
      editor?.chain().focus().unsetLink().run();
    }
    setShowLinkDialog(false);
    setLinkUrl('');
  };

  if (!editor) {
    return null;
  }

  if (showHtmlSource) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', ...sx }}>
        {showToolbar && (
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={toggleHtmlView}
              startIcon={<HtmlIcon />}
              variant="outlined"
            >
              WYSIWYG View
            </Button>
          </Box>
        )}
        <TextField
          multiline
          fullWidth
          value={htmlSource}
          onChange={(e) => setHtmlSource(e.target.value)}
          placeholder="HTML source code..."
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              height: '100%',
              alignItems: 'flex-start',
              fontFamily: 'monospace',
              fontSize: '13px',
            },
            '& textarea': {
              height: '100% !important',
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', ...sx }}>
      {showToolbar && !readOnly && (
        <Box
          sx={{
            mb: 1,
            p: 1,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            bgcolor: 'grey.50',
          }}
        >
          {/* Heading Levels */}
          <ButtonGroup size="small" variant="outlined">
            <Button
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              variant={editor.isActive('heading', { level: 1 }) ? 'contained' : 'outlined'}
            >
              H1
            </Button>
            <Button
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              variant={editor.isActive('heading', { level: 2 }) ? 'contained' : 'outlined'}
            >
              H2
            </Button>
            <Button
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              variant={editor.isActive('heading', { level: 3 }) ? 'contained' : 'outlined'}
            >
              H3
            </Button>
          </ButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Text Formatting */}
          <ButtonGroup size="small" variant="outlined">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleBold().run()}
              color={editor.isActive('bold') ? 'primary' : 'default'}
            >
              <BoldIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              color={editor.isActive('italic') ? 'primary' : 'default'}
            >
              <ItalicIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              color={editor.isActive('underline') ? 'primary' : 'default'}
            >
              <UnderlineIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleStrike().run()}
              color={editor.isActive('strike') ? 'primary' : 'default'}
            >
              <StrikeIcon fontSize="small" />
            </IconButton>
          </ButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Colors */}
          <ButtonGroup size="small" variant="outlined">
            <IconButton
              size="small"
              onClick={(e) => setTextColorAnchor(e.currentTarget)}
            >
              <TextColorIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => setHighlightAnchor(e.currentTarget)}
            >
              <HighlightIcon fontSize="small" />
            </IconButton>
          </ButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Lists */}
          <ButtonGroup size="small" variant="outlined">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              color={editor.isActive('bulletList') ? 'primary' : 'default'}
            >
              <BulletListIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              color={editor.isActive('orderedList') ? 'primary' : 'default'}
            >
              <OrderedListIcon fontSize="small" />
            </IconButton>
          </ButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Alignment */}
          <ButtonGroup size="small" variant="outlined">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
            >
              <AlignLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
            >
              <AlignCenterIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
            >
              <AlignRightIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().setTextAlign('justify').run()}
              color={editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'}
            >
              <AlignJustifyIcon fontSize="small" />
            </IconButton>
          </ButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Blockquote & Code */}
          <ButtonGroup size="small" variant="outlined">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              color={editor.isActive('blockquote') ? 'primary' : 'default'}
            >
              <BlockquoteIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
              color={editor.isActive('codeBlock') ? 'primary' : 'default'}
            >
              <CodeIcon fontSize="small" />
            </IconButton>
          </ButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Link */}
          <IconButton
            size="small"
            onClick={() => {
              const previousUrl = editor.getAttributes('link').href;
              setLinkUrl(previousUrl || '');
              setShowLinkDialog(true);
            }}
            color={editor.isActive('link') ? 'primary' : 'default'}
          >
            <LinkIcon fontSize="small" />
          </IconButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* HTML Source View */}
          <Button
            size="small"
            onClick={toggleHtmlView}
            startIcon={<HtmlIcon />}
            variant="outlined"
          >
            HTML
          </Button>
        </Box>
      )}

      {/* Text Color Menu */}
      <Menu
        anchorEl={textColorAnchor}
        open={Boolean(textColorAnchor)}
        onClose={() => setTextColorAnchor(null)}
      >
        <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {colors.map((color) => (
            <Box
              key={color}
              onClick={() => setTextColor(color)}
              sx={{
                width: 24,
                height: 24,
                bgcolor: color,
                border: '1px solid',
                borderColor: 'grey.400',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'scale(1.2)',
                },
              }}
            />
          ))}
        </Box>
      </Menu>

      {/* Highlight Color Menu */}
      <Menu
        anchorEl={highlightAnchor}
        open={Boolean(highlightAnchor)}
        onClose={() => setHighlightAnchor(null)}
      >
        <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {colors.map((color) => (
            <Box
              key={color}
              onClick={() => setHighlightColor(color)}
              sx={{
                width: 24,
                height: 24,
                bgcolor: color,
                border: '1px solid',
                borderColor: 'grey.400',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'scale(1.2)',
                },
              }}
            />
          ))}
        </Box>
      </Menu>

      {/* Link Dialog */}
      {showLinkDialog && (
        <Box
          sx={{
            mb: 1,
            p: 2,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            bgcolor: 'grey.50',
          }}
        >
          <TextField
            fullWidth
            size="small"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
            label="URL"
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" onClick={setLink}>
              Set Link
            </Button>
            <Button size="small" onClick={() => setShowLinkDialog(false)}>
              Cancel
            </Button>
            {editor.isActive('link') && (
              <Button
                size="small"
                color="error"
                onClick={() => {
                  editor.chain().focus().unsetLink().run();
                  setShowLinkDialog(false);
                }}
              >
                Remove Link
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Editor Content */}
      <Box
        sx={{
          flex: 1,
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 1,
          p: 2,
          minHeight: 200,
          bgcolor: 'white',
          // Keep these rules in sync with the shadow CSS in TiptapViewer.tsx
          // so authoring is WYSIWYG. The viewer is the source of truth for
          // typography — this just mirrors it inside ProseMirror.
          '& .ProseMirror': {
            outline: 'none',
            minHeight: 200,
            fontSize: 16,
            lineHeight: 1.6,
            color: 'rgba(0, 0, 0, 0.87)',
            '& p.is-editor-empty:first-of-type::before': {
              color: 'grey.400',
              content: `attr(data-placeholder)`,
              float: 'left',
              height: 0,
              pointerEvents: 'none',
            },
            '& p, & ul, & ol, & blockquote, & pre, & table': { margin: '0.75em 0' },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              margin: '1em 0 0.5em',
              lineHeight: 1.3,
              fontWeight: 600,
            },
            '& > *:first-child': { marginTop: 0 },
            '& > *:last-child': { marginBottom: 0 },
            '& h1': { fontSize: '2em' },
            '& h2': { fontSize: '1.5em' },
            '& h3': { fontSize: '1.25em' },
            '& h4': { fontSize: '1.1em' },
            '& ul, & ol': { paddingLeft: '1.75em' },
            '& li': { marginBottom: '0.4em' },
            '& li > ul, & li > ol': { margin: '0.4em 0' },
            '& li:last-child': { marginBottom: 0 },
            '& a': {
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
            },
            '& table': { borderCollapse: 'collapse', width: 'auto' },
            '& th, & td': {
              padding: '0.5em 0.75em',
              border: '1px solid',
              borderColor: 'grey.300',
              verticalAlign: 'top',
            },
            '& th': { backgroundColor: 'grey.100', fontWeight: 600, textAlign: 'left' },
            '& blockquote': {
              borderLeft: '4px solid',
              borderColor: 'grey.300',
              padding: '0.25em 0 0.25em 1em',
              marginLeft: 0,
              color: 'rgba(0, 0, 0, 0.7)',
            },
            '& code': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              borderRadius: '3px',
              padding: '0.15em 0.35em',
              fontFamily: 'monospace',
            },
            '& pre': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              borderRadius: '5px',
              padding: '0.75em 1em',
              fontFamily: 'monospace',
              overflowX: 'auto',
              '& code': {
                backgroundColor: 'transparent',
                padding: 0,
              },
            },
            '& img': { maxWidth: '100%', height: 'auto' },
          },
        }}
      >
        <EditorContent editor={editor} placeholder={placeholder} />
      </Box>
    </Box>
  );
};

export default TiptapEditor;
